import React, { useEffect, useRef, useState } from 'react';
import { Alert, Modal, Spin } from 'antd';
import { catalogueRequest } from './api';
import {
  CatalogueEntry,
  CatalogueRow,
  FamilyPreparation,
  FamilySelectionContext,
} from './types';

export default function AdditionReview({
  entries,
  byId,
  selected,
  context,
  disabled,
  onClose,
  onAdd,
}: {
  entries: CatalogueEntry[];
  byId: Map<number, CatalogueRow>;
  selected: Set<number>;
  context?: FamilySelectionContext;
  disabled?: boolean;
  onClose: () => void;
  onAdd: (ids: number[]) => void;
}) {
  const [preview, setPreview] = useState<FamilyPreparation>();
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const controller = useRef<AbortController>();
  const blocked = useRef(disabled);
  blocked.current = disabled;
  const families = entries
    .flatMap(entry => (entry.family ? [entry.family.id] : []))
    .join(',');
  const sessionId = context?.sessionId;
  const historical = context?.historical;
  const live = context?.live;
  useEffect(() => {
    const abort = new AbortController();
    controller.current = abort;
    setError('');
    setPreview(undefined);
    if (families) {
      if (!sessionId)
        setError('A saved session is needed to prepare a family.');
      else if (!historical && !live)
        setError('Bond families are available only for historical sessions.');
      else {
        setLoading(true);
        catalogueRequest<FamilyPreparation>(
          'families/prepare',
          'GET',
          { session_id: sessionId, families },
          abort.signal,
        )
          .then(result => {
            if (!abort.signal.aborted) setPreview(result);
          })
          .catch(reason => {
            if (!abort.signal.aborted) setError(reason.message);
          })
          .finally(() => {
            if (!abort.signal.aborted) setLoading(false);
          });
      }
    }
    return () => abort.abort();
  }, [families, sessionId, historical, live, context?.key]);

  const resolved = (entry: CatalogueEntry, result = preview) =>
    entry.family
      ? result?.families.find(family => family.family_id === entry.family!.id)
      : undefined;
  const idsFor = (entry: CatalogueEntry) =>
    entry.family
      ? resolved(entry)?.asset_ids || []
      : entry.members.map(row => row.id);
  const invalid = entries.some(
    entry =>
      entry.family &&
      (resolved(entry)?.status !== 'ready' ||
        !resolved(entry)?.asset_ids.length ||
        resolved(entry)?.asset_ids.some(
          id =>
            !Number.isInteger(id) ||
            byId.get(id)?.bond_family?.id !== entry.family!.id,
        )),
  );
  const ids = [...new Set(entries.flatMap(idsFor))].filter(
    id => !selected.has(id),
  );
  const confirm = async () => {
    if (disabled || invalid || error || loading || (!ids.length && !families))
      return;
    const abort = controller.current!;
    if (families) {
      if (!context?.historical && !context?.live) return;
      setLoading(true);
      try {
        const fresh = await catalogueRequest<FamilyPreparation>(
          'families/prepare',
          'GET',
          { session_id: sessionId, families },
          abort.signal,
        );
        if (abort.signal.aborted || blocked.current) return;
        if (fresh.fingerprint !== preview?.fingerprint) {
          setPreview(fresh);
          setNotice(
            'The session settings or available issues changed. Review the updated selection before adding.',
          );
          return;
        }
        if (!live && !fresh.context.afs_bond_switchover_enabled) {
          setCommitting(true);
          await context.enableRollover(abort.signal);
        }
      } catch (reason) {
        if (!abort.signal.aborted) setError((reason as Error).message);
        return;
      } finally {
        if (!abort.signal.aborted) {
          setLoading(false);
          setCommitting(false);
        }
      }
    }
    if (!abort.signal.aborted && !blocked.current) {
      onAdd(ids);
      onClose();
    }
  };
  return (
    <Modal
      open
      title={`Add ${entries.length} instruments`}
      onCancel={() => !committing && onClose()}
      closable={!committing}
      maskClosable={!committing}
      keyboard={!committing}
      cancelButtonProps={{ disabled: committing }}
      onOk={confirm}
      okText="Add instruments"
      confirmLoading={loading}
      okButtonProps={{
        disabled:
          disabled ||
          invalid ||
          !!error ||
          loading ||
          (!ids.length && !families),
      }}
    >
      <p>
        Existing selections are retained. Each synthetic bond family counts as
        one instrument.
      </p>
      {preview && !live && (
        <p className="ac-muted">
          Saved session window: {preview.context.start_date.slice(0, 10)} →{' '}
          {preview.context.end_date.slice(0, 10)} ·{' '}
          {preview.context.landing_dates.length} calendar dates checked.
        </p>
      )}
      {notice && <Alert type="info" title={notice} />}
      {error && <Alert type="warning" title={error} />}
      {loading && <Spin />}
      <div className="ac-bulk-list">
        {entries.map(entry => (
          <div key={entry.key} className="ac-review-entry">
            {entry.family ? (
              <>
                <strong>{entry.family.code} · Synthetic bond family</strong>
                {resolved(entry)?.status === 'manual' && (
                  <Alert type="warning" title={resolved(entry)?.reason} />
                )}
                {resolved(entry)?.status === 'ready' && (
                  <span className="ac-muted">
                    {live ? 'Latest issue resolved when the session starts' : `${idsFor(entry).length} issues selected automatically`}
                  </span>
                )}
              </>
            ) : (
              <span>
                {entry.members[0].ticker} · {entry.members[0].name}
              </span>
            )}
          </div>
        ))}
      </div>
      {families && preview && !invalid && !live && (
        <p className="ac-muted">
          Bond rollover will be enabled when you add these instruments.
        </p>
      )}
      {invalid &&
        preview &&
        !preview.families.some(family => family.status === 'manual') && (
          <Alert
            type="warning"
            title="The issue list changed. Close this review and refresh the catalogue before adding."
          />
        )}
      {live && families && (
        <p className="ac-muted">Select the family once. New quarterly issues are added automatically; the Rollover bonds setting controls whether existing positions transfer.</p>
      )}
      {!live && !invalid && !error && !loading && (
        <p className="ac-muted">
          {ids.length} concrete instruments will be added
          {families
            ? '; family issues may extend beyond the current catalogue filters'
            : ''}
          .
        </p>
      )}
    </Modal>
  );
}
